<template>
  <div>
    <v-data-table
      class="sn-table mt-4"
      :class="{ 'clickable-rows': (clickableRows && items.length > 0), 'hide-no-data': hideNoData }"
      :fixed-header="fixedHeader"
      :headers="headersWithActions"
      :items="loadingItems || items"
      :item-key="itemKey"
      :page="parseInt(pagination.page)"
      :items-per-page="itemsPerPage"
      hide-default-footer
      calculate-widths
      data-cy="table-overview"
      :item-class="rowDisabled"
      @click:row="handleRowClick"
    >
      <!--<editor-fold desc="Skeleton Loader">-->

      <template
        v-if="loading && validHeaders.length"
        #item
      >
        <tr>
          <td
            v-for="(header, headerIndex) in headersWithActions"
            :key="`loading-header-${headerIndex}`"
          >
            <v-skeleton-loader
              :width="getRandomSkeletonLoaderWidth()"
              type="text"
            />
          </td>
        </tr>
      </template>

      <!--</editor-fold>-->

      <!--<editor-fold desc="Empty State">-->

      <template #no-data>
        <div data-cy="empty-list">
          <v-icon size="150">
            $snEmptyBox
          </v-icon>
          <p class="sn-body-1">
            {{ emptyListText }}
          </p>
        </div>
      </template>

      <!--</editor-fold>-->

      <!--<editor-fold desc="Top Slot">-->

      <template
        v-if="showTopSlot"
        #top
      >
        <div class="mb-4">
          <slot name="top">
            <default-top
              :hide-search="hideSearch"
              :search-placeholder="searchPlaceholder"
              :search-text="searchText"
              :actions="tableActions"
              :filtered-headers="filteredHeaders"
              v-on="$listeners"
              @chip-filters:change="handleFiltersChanged"
              @chip-filters:load-more="$emit('filters:load-more', $event)"
            />
          </slot>
        </div>
      </template>

      <!--</editor-fold>-->

      <!--<editor-fold desc="Sort and Filter Headers">-->

      <template
        v-for="header in headersNeedingFilterMenu"
        #[`header.${header.value}`]
      >
        <sort-and-filter-header
          :key="header.value"
          :header="header"
          :cy-tag="header.text"
          :active-sort="activeSort"
          @header-sort:change="handleSortChanged"
          @header-filters:search="$emit('filters:search', $event)"
          @header-filters:change="handleFiltersChanged"
          @header-filters:load-more="$emit('filters:load-more', $event)"
        />
      </template>

      <!--</editor-fold>-->

      <!--<editor-fold desc="Column Type Slot">-->

      <template
        v-for="(slot, index) in Object.keys(itemSlots)"
        #[slot]="props"
      >
        <div
          :key="index"
        >
          <slot
            :name="slot"
            v-bind="props"
          />
        </div>
      </template>

      <!--</editor-fold>-->

      <!--<editor-fold desc="Item Actions">-->

      <!-- Header with Action Buttons -->
      <template
        v-if="showItemActionsHeader"
        #header
      >
        <item-actions-header
          :actions="itemActions"
          :num-selected="numSelected"
          :indeterminate="indeterminate"
          :selected-items="selectedItems"
          :num-featured-actions="parseInt(numFeaturedActions)"
          v-on="$listeners"
          @header-select="toggleSelectAll"
        />
      </template>

      <!-- Select All Checkbox -->
      <template
        v-if="areSomeMultiSelectItemActions"
        #header.selected
      >
        <v-simple-checkbox
          :value="selectedItems.length > 0"
          :indeterminate="indeterminate"
          off-icon="$snCheckBox"
          on-icon="$snCheckBoxSelected"
          class="chkbx"
          color="snBrand"
          :data-pendo="pendo + '-SelectAll'"
          :ripple="false"
          data-cy="table-header-select-all-checkbox"
          @input="toggleSelectAll"
        />
      </template>

      <!-- Single Item Checkbox -->
      <template
        v-if="areSomeMultiSelectItemActions"
        #item.selected="{ item }"
      >
        <v-simple-checkbox
          :value="selectedItemIds.includes(item[itemKey])"
          off-icon="$snCheckBox"
          on-icon="$snCheckBoxSelected"
          class="chkbx"
          :class="{ 'selected': selectedItemIds.includes(item[itemKey]) }"
          color="snBrand"
          :data-cy="`${item.name || item[validHeaders[0].value]}-checkbox`"
          data-test="row-items"
          :data-pendo="pendo + '-SelectRow'"
          :ripple="false"
          @input="toggleSingleSelect(item)"
        />
      </template>

      <!--</editor-fold>-->

      <!--<editor-fold desc="Three-dot Menu Actions">-->

      <template
        v-if="areItemActions"
        #item.actions="{ item }"
      >
        <item-actions-three-dot-menu
          v-if="itemActions.length"
          :item="item"
          :item-actions="itemActions"
          v-on="$listeners"
        />
      </template>

      <!--</editor-fold>-->

      <!--<editor-fold desc="Pagination">-->

      <template #footer>
        <!-- Loading State -->
        <div
          v-if="loading"
          class="snsl-table-tfoot"
        >
          <v-skeleton-loader
            width="50px"
            type="text"
            class="mr-2"
          />
          <v-skeleton-loader
            width="100px"
            type="text"
          />
        </div>

        <!-- Pagination -->
        <div
          v-else-if="showPagination"
          class="d-flex flex-row-reverse py-6"
        >
          <sn-pagination
            :pagination="pagination"
            :per-page-options="perPageOptions"
            :filtered-total="calculatedTotalNumberOfItems"
            :sn-input-props_pendo="pendo"
            v-on="$listeners"
          />
        </div>
      </template>

      <!--</editor-fold>-->
    </v-data-table>
  </div>
</template>

<script>
import ItemActionsHeader from './coreComponents/itemActions/itemActionsHeader'
import ItemActionsThreeDotMenu from './coreComponents/itemActions/itemActionsThreeDotMenu'
import SortAndFilterHeader from './coreComponents/sortAndFilter/header'
import DefaultTop from './coreComponents/defaultTopSlot'
import SnPagination from '~/components/snui/v2/snPagination/snPagination'
import { cloneDeep } from 'lodash'

export default {
  name: 'SnTable',
  components: {
    ItemActionsHeader,
    ItemActionsThreeDotMenu,
    SortAndFilterHeader,
    DefaultTop,
    SnPagination
  },
  props: {
    activeSort: {
      type: String,
      required: false,
      default: ''
    },
    // <!--<editor-fold desc="Loading State">-->
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    numLoadingRows: {
      type: Number,
      required: false,
      default: 10
    },
    // <!--</editor-fold>-->

    // <!--<editor-fold desc="Empty State">-->
    emptyListText: {
      type: String,
      required: false,
      default: 'No data available'
    },
    // <!--</editor-fold>-->

    // <!--<editor-fold desc="Top Slot">-->
    hideSearch: {
      type: Boolean,
      required: false,
      default: false
    },
    hideNoData: {
      type: Boolean,
      required: false,
      default: false
    },
    searchPlaceholder: {
      type: String,
      required: false,
      default: 'Search'
    },
    searchText: {
      type: String,
      required: false,
      default: ''
    },
    showSearch: {
      type: Boolean,
      required: false,
      default: true
    },
    tableActions: {
      type: Array,
      required: false,
      default: () => []
    },
    // <!--</editor-fold>-->

    // <!--<editor-fold desc="Table Headers">-->
    headers: {
      type: Array,
      required: true
    },
    fixedHeader: {
      type: Boolean,
      required: false,
      default: false
    },
    numFeaturedActions: {
      type: [Number, String],
      required: false,
      default: 3
    },
    // <!--</editor-fold>-->

    // <!--<editor-fold desc="Items">-->
    items: {
      type: Array,
      required: true
    },
    preselectedItemIds: {
      type: Array,
      required: false,
      default: () => []
    },
    itemKey: {
      type: String,
      required: false,
      default: 'id'
    },
    clickableRows: {
      type: Boolean,
      required: false,
      default: false
    },
    itemActions: {
      type: Array,
      required: false,
      default: () => []
    },
    clearSelectedItemsOnUpdate: {
      type: Boolean,
      required: false,
      default: false
    },
    // <!--</editor-fold>-->

    // <!--<editor-fold desc="Pagination">-->
    pagination: {
      type: Object,
      required: false,
      default: () => ({
        page: 1,
        itemsPerPage: 10,
        sortBy: '',
        sortDesc: true,
        inMemoryPagination: false
      })
    },
    totalItems: {
      type: Number,
      required: false,
      default: 0
    },
    // <!--<editor-fold desc="Pendo">-->
    pendo: {
      type: String,
      required: false,
      default: ''
    }
    // <!--</editor-fold>-->
  },
  data () {
    return {
      selectedItems: [],
      selectedFilters: [],
      perPageOptions: [10, 25, 50]
    }
  },
  computed: {
    itemSlots () {
      let itemSlots = {}

      for (const key in this.$scopedSlots) {
        if (key.includes('item.')) {
          itemSlots = {
            ...itemSlots,
            [key]: this.$scopedSlots[key]
          }
        }
      }

      return itemSlots
    },
    itemsPerPage () {
      const inMemory = this.pagination.inMemoryPagination
      return inMemory ? parseInt(this.pagination.itemsPerPage) : -1
    },
    loadingItems () {
      // The rows returned from this are used as placeholders
      // for showing each row when loading. The content of each
      // row ultimately doesn't matter, but they should be objects
      // since `items` in `v-data-table` expects an array of objects

      if (!this.loading) return null

      const loadingRows = []

      for (let i = 0; i < this.numLoadingRows; i++) {
        loadingRows.push({ name: 'loadingRow' })
      }

      return loadingRows
    },
    validHeaders () {
      // Ensure that each header has a 'sortable' property.
      // Set to `false` because we're not using Vuetify's built in sorting
      return this.headers.map(header => ({
        ...header,
        sortable: false
      }))
    },
    headersNeedingFilterMenu () {
      return this.validHeaders.filter(f => f.sortBy || f.filterBy)
    },
    headersWithActions () {
      if (!this.areItemActions) return this.headers

      const headersWithActions = [
        ...this.validHeaders,
        {
          text: '',
          value: 'actions',
          width: '20px',
          sortable: false
        }
      ]

      if (this.areSomeMultiSelectItemActions) {
        headersWithActions.unshift({
          text: '',
          value: 'selected',
          width: '20px',
          sortable: false
        })
      }

      return headersWithActions
    },
    showTopSlot () {
      const hasFilters = this.headers?.some(h => {
        const nodes = h.filterBy?.nodes ?? h.filterBy
        return !!nodes?.some(a => a.active)
      })

      return !!this.$scopedSlots.top || !this.hideSearch || this.tableActions.length || hasFilters
    },
    areItemActions () {
      return this.itemActions?.length > 0 ?? false
    },
    areSomeMultiSelectItemActions () {
      return this.items.length && !this.loading && this.areItemActions && this.itemActions.some(action => !action.singleItemOnly)
    },
    showItemActionsHeader () {
      return this.areItemActions && (this.allSelected || this.indeterminate)
    },
    allSelected () {
      return this.numSelected > 0 && !this.indeterminate
    },
    selectedItemIds () {
      return this.selectedItems.map(i => i[this.itemKey])
    },
    indeterminate () {
      const numItems = this.items.length
      return this.numSelected > 0 && this.numSelected < numItems
    },
    numSelected () {
      return this.selectedItems?.length ?? 0
    },
    calculatedTotalNumberOfItems () {
      return this.totalItems || this.items.length
    },
    filteredHeaders () {
      return this.validHeaders
        .map(h => {
          if (!h.filterBy) return null

          const rawFilters = h.filterBy.nodes ?? h.filterBy

          if (rawFilters.some(f => f.active)) return h
          else return null
        })
        .filter(col => !!col)
    },
    showPagination () {
      return this.items.length && this.calculatedTotalNumberOfItems > this.perPageOptions[0]
    },
    rowDisabled (item) {
      return item.disabled ? 'disabled' : null
    }
  },
  watch: {
    selectedItems: {
      deep: true,
      handler (items) {
        this.$emit('selected-items-changed', items)
      }
    },
    loading () {
      this.selectedItems = []
    },
    items: {
      deep: true,
      handler (items) {
        // make sure the selectedItems are updated with any data changes unless table specifically wants them cleared out
        const itemKeys = this.selectedItems.map(item => item[this.itemKey])
        this.selectedItems = this.clearSelectedItemsOnUpdate ? [] : items.filter(item => itemKeys.includes(item[this.itemKey]))
      }
    }
  },
  mounted () {
    this.selectedItems = this.items.filter((item) => this.preselectedItemIds.includes(item.guid))
  },
  methods: {
    // Loading State
    getRandomSkeletonLoaderWidth () {
      const min = 10
      const max = 50
      return `${Math.floor((Math.random() * (max - min + 1)) + min)}%`
    },

    // Header Actions
    toggleSingleSelect (item) {
      if (this.selectedItemIds.includes(item[this.itemKey])) {
        this.selectedItems = this.selectedItems.filter(i => i[this.itemKey] !== item[this.itemKey])
      } else {
        this.selectedItems = [
          ...this.selectedItems,
          item
        ]
      }
    },
    toggleSelectAll () {
      if (this.selectedItems.length === 0) {
        // If nothing selected -> select all
        this.selectedItems = cloneDeep(this.items)
      } else {
        // If some or all are selected -> select none
        this.selectedItems = []
      }
    },

    // Sort and Filter
    handleSortChanged (sort) {
      this.$emit('sort:change', sort)
    },
    handleFiltersChanged (filterChanges) {
      const { col, filters } = filterChanges

      filterChanges.filters = this.processFilters(col, filters)
      filterChanges.allActiveTableFilters = this.processAllActiveTableFilters(col, filters)

      this.$emit('filters:change', filterChanges)
    },
    processFilters (col, filters) {
      const filteredHeader = this.headers.find(h => h.value === col)

      if (filteredHeader.filterBy.nodes) {
        const changedFilterIds = filters.nodes.map(f => f.value)
        const filtersWithChanges = filteredHeader.filterBy.nodes.map(f => {
          if (changedFilterIds.includes(f.value)) {
            return filters.nodes.find(cf => cf.value === f.value)
          }

          return f
        })

        return {
          ...filteredHeader.filterBy,
          nodes: filtersWithChanges
        }
      } else {
        const rawHeaderFilters = filteredHeader.filterBy.nodes ?? filteredHeader.filterBy
        const rawChangedFilters = filters.nodes ?? filters
        return this.mergeFilterChanges(rawHeaderFilters, rawChangedFilters)
      }
    },
    processAllActiveTableFilters (col, filters) {
      const allActiveTableFilters = {}

      for (const header of this.headers) {
        if (!header.filterBy) continue

        const filterSource = header.value === col ? filters : header.filterBy
        const rawFilters = filterSource.nodes ?? filterSource
        const areActiveFilters = rawFilters?.some(f => f.active) ?? false

        if (areActiveFilters) {
          allActiveTableFilters[header.value] = rawFilters.filter(f => f.active).map(f => f.value)
        }
      }

      return allActiveTableFilters
    },
    mergeFilterChanges (original, changes) {
      return original.map(oFilter => changes.find(f => oFilter.value === f.value) || oFilter)
    },

    // Row Click
    handleRowClick (event) {
      if (!this.clickableRows) return

      try {
        pendo.track(this.snInputProps_pendo + '-RowClicked') // eslint-disable-line no-undef
      } catch (e) {
        if (e?.name !== 'ReferenceError') {
          this.$sentry.captureException(e)
        }
      }

      this.$emit('click:row', event)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.sn-switch ::v-deep {
  :not(.v-input--is-label-active) .v-input--switch__track {
    color: $sn-gray-04;
  }

  .v-input--switch {
    &__track {
      border: none;
      opacity: 1;
    }

    &__thumb {
      width: 22px;
      height: 22px;
      top: calc(50% - 11px);
      margin-left: -1px;
    }
  }
}

.snsl-table-tfoot {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  padding: 16px;
}

.sn-table {
  border-collapse: collapse;
  width: 100%;

  ::v-deep .v-data-table__wrapper table {
    border-radius: 2px 2px 0 0;
    position: relative;

    thead tr th {
      background-color: $sn-brand-01 !important;
      color: $sn-primary-03 !important;
      font-family: $font-family-primary !important;
      font-size: 15px;
      font-weight: $font-weight-semi-bold !important;
      padding-left: 12px;
      padding-right: 12px;
      border-bottom: none !important;
      white-space: nowrap;

      &:first-child{
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px
      }

      &:last-child{
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px
      }
    }

    tbody tr {
      background-color: $sn-white !important;
      height: 48px;

      &:has(.selected) {
        background-color: $sn-brand-01 !important;
        &:hover {
          background-color: $sn-brand-01 !important;
        }
        td:not(.v-data-table__mobile-row) {
          border-top: 1px solid $sn-brand-02 !important;
          border-bottom: 1px solid $sn-brand-02 !important;
        }
      }

      &:hover {
        background: transparent !important;
      }

      &:not(.v-data-table__empty-wrapper) td:not(.v-data-table__mobile-row) {
        border-bottom: 2px solid $sn-gray-01 !important;
      }

      td {
        color: $sn-primary-03 !important;
        font-family: $font-family-primary;
        font-size: 15px;
        font-weight: $font-weight-regular;
        padding: 12px;
      }
    }

    tbody .disabled {
      opacity: 0.4;
      pointer-events: none;
    }

    .actions-column {
      min-width: 48px;
    }

    .v-simple-checkbox {
      i, .v-icon {
        color: $sn-primary;
      }
    }
  }

  &.hide-no-data {
    ::v-deep .v-data-table__empty-wrapper {
      display: none;
    }
  }
}

.sn-table.clickable-rows {
  ::v-deep .v-data-table__wrapper table {
    tbody tr {
      cursor: pointer;
    }
    tr:hover td {
      background-color: $sn-gray-01;
    }
  }
}
</style>
